import React from "react";
import { Platform } from "react-native";

import { G, Path, Text } from "react-native-svg";

import { BOARD_BG } from "../../theme/boardColors";
import type { BoardIndicator } from "./boardLayout";
import {
  LABEL_FONT_FAMILY,
  LABEL_HALO,
  LABEL_WEIGHT,
  PATH_LABEL_FONT,
} from "./constants";

const PAINT_ORDER = Platform.OS === "web";
const ARROW = "M 8 0 L -5 -7 L -5 7 Z";

interface BoardIndicatorsProps {
  indicators: BoardIndicator[];
  colors: { start: string; target: string };
  textColor: string;
}

/**
 * Edge arrows toward words the focus fit left off the board (the start),
 * with the word's name beside the arrow on the board side. Screen-fixed.
 */
const BoardIndicators: React.FC<BoardIndicatorsProps> = ({
  indicators,
  colors,
  textColor,
}) => (
  <G>
    {indicators.map((ind) => {
      const fill = ind.endpoint === "start" ? colors.start : colors.target;
      const c = Math.cos(ind.angle);
      const s = Math.sin(ind.angle);
      const side = Math.abs(c) >= Math.abs(s);
      const anchor = side ? (c > 0 ? "end" : "start") : "middle";
      const tx = side ? ind.x - Math.sign(c) * 14 : ind.x;
      const ty = side ? ind.y + 4 : ind.y - Math.sign(s) * 14 + 4;
      const text = {
        x: tx,
        y: ty,
        textAnchor: anchor as "end" | "start" | "middle",
        fontSize: PATH_LABEL_FONT,
        fontWeight: LABEL_WEIGHT,
        fontFamily: LABEL_FONT_FAMILY,
      };
      const halo = {
        stroke: BOARD_BG,
        strokeWidth: LABEL_HALO,
        strokeLinejoin: "round" as const,
      };
      return (
        <G key={ind.id} testID={`board-indicator-${ind.id}`}>
          <G
            transform={`translate(${ind.x},${ind.y}) rotate(${(ind.angle * 180) / Math.PI})`}
          >
            <Path d={ARROW} fill={fill} stroke={BOARD_BG} strokeWidth={1.5} />
          </G>
          {PAINT_ORDER ? (
            <Text
              {...text}
              {...halo}
              {...({ paintOrder: "stroke" } as object)}
              fill={textColor}
            >
              {ind.id}
            </Text>
          ) : (
            <>
              <Text {...text} {...halo} fill={BOARD_BG}>
                {ind.id}
              </Text>
              <Text {...text} fill={textColor}>
                {ind.id}
              </Text>
            </>
          )}
        </G>
      );
    })}
  </G>
);

export default BoardIndicators;
